import { useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../redux/app/hooks';
import { setHostContext } from '../../redux/features/hostContext/hostContextSlice';
import { useChat } from './useChat';
import { HostContext } from '../../embed-script/HostContext';
import { isMessageFromHost } from '../../embed-script/messageOrigin';
import { notifyPanelHidden } from '../panelVisibility';
import { getParentTargetOrigin, rememberParentOrigin } from '../parentOrigin';
import { RootState } from '../../redux/app/store';
import { IEventTelemetry } from '@microsoft/applicationinsights-web';
import * as tracking from '../../libs/tracking';
import { AppState } from '../../AppState';
import { TFunction } from 'i18next';

interface UseMessageHandlerProps {
    setAppState: (state: AppState) => void;
    i18n: {
        changeLanguage: (lang: string) => Promise<TFunction>;
    };
    setShowCsat?: (show: boolean) => void;
}

interface HostContextMessageData {
    event: string;
    data: unknown;
}

export const useMessageHandler = ({ setAppState, i18n, setShowCsat }: UseMessageHandlerProps) => {
    const dispatch = useAppDispatch();
    const chat = useChat();

    // Get access to chat messages from Redux store
    const chatMessages = useAppSelector(
        (state: RootState) =>
            (
                state.conversations.conversations[state.conversations.selectedId] as
                    (typeof state.conversations.conversations)[string] | undefined
            )?.messages,
    );
    const enableCsatSurvey = useAppSelector((state) => state.app.serviceInfo.featureFlags.EnableCsatSurvey);
    const enableInChatCsat = useAppSelector((state) => state.app.serviceInfo.featureFlags.EnableInChatCsat);
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);

    const setHostContextState = useCallback(
        (newHostContext: HostContext) => {
            dispatch(setHostContext(newHostContext));
        },
        [dispatch],
    );

    const handleContextChange = useCallback(
        async (data: HostContext) => {
            console.log('Context changed:', data);
            setHostContextState(data);

            if (data.language) {
                try {
                    await i18n.changeLanguage(data.language);
                    // Only trigger chat loading if not in embedded mode to prevent focus stealing
                    if (chatMessages?.length === 1 && !embeddedMode) {
                        await chat.loadChats(data);
                        setAppState(AppState.Chat);
                    }
                } catch (e) {
                    console.error('Error handling context change:', e);
                }
            }
            localStorage.removeItem('hostContext');
        },
        [setHostContextState, i18n, chat, setAppState, chatMessages, embeddedMode],
    );

    const handleCsatCheck = useCallback(async () => {
        if (!setShowCsat) return;

        // #1641: when the inline CSAT widget is active, the legacy modal path must never be
        // triggered — not even the iframe resize/re-show the host performs on `showCsat`. The
        // backend `/csat/status` already returns false for this cohort; this is a client-side
        // backstop so a regression there can't surface the modal. Post `skipCsat` so the host
        // finishes its close sequence cleanly.
        if (enableInChatCsat) {
            window.parent.postMessage({ event: 'skipCsat' }, getParentTargetOrigin());
            return;
        }

        const shouldShow = await chat.shouldShowCsat();

        if (shouldShow && enableCsatSurvey) {
            setShowCsat(true);
            window.parent.postMessage({ event: 'showCsat' }, getParentTargetOrigin());
        } else {
            window.parent.postMessage({ event: 'skipCsat' }, getParentTargetOrigin());
        }
    }, [chat, setShowCsat, enableCsatSurvey, enableInChatCsat]);

    const handleMessage = useCallback(
        (e: MessageEvent<HostContextMessageData>) => {
            // CWE-346: ignore messages that don't originate from our embedding parent frame, and
            // treat the payload as untrusted — postMessage data is arbitrary regardless of its type.
            // Inline dup of the source check so Coverity's UNCHECKED_ORIGIN sees it — rationale in messageOrigin.ts.
            const payload: unknown = e.data;
            if (e.source !== window.parent || !isMessageFromHost(e) || !payload || typeof payload !== 'object') return;
            // CWE-668 (#2113): this message passed the source check, so its origin is our real
            // embedder — remember it as the target for iframe → host posts (see parentOrigin.ts).
            rememberParentOrigin(e.origin);
            switch (e.data.event) {
                case 'aiassitantcontextchanged': {
                    const jsonString = e.data.data as string;
                    const data = JSON.parse(jsonString) as HostContext;
                    void handleContextChange(data);
                    break;
                }
                case 'aiassitantcontextupdate': {
                    const data = e.data.data as HostContext;
                    void handleContextChange(data);
                    break;
                }
                case 'aiassitanttrackevent': {
                    const data = e.data.data as IEventTelemetry;
                    tracking.trackEvent(data.name, data.properties);
                    break;
                }
                case 'checkCsat': {
                    void handleCsatCheck();
                    break;
                }
                case 'panelHidden': {
                    notifyPanelHidden();
                    break;
                }
            }
        },
        [handleContextChange, handleCsatCheck],
    );

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);
};
